// frontend/src/pages/AssessmentResultsPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import authAxios from '../auth/authAxios';

interface UserResponse {
  response_id: string;
  question_id: string;
  user_answer: string;
  ai_feedback?: string;
  response_time_seconds: number;
  generated_question: {
    question_text: string;
    question_type: string;
    skill_focus?: string;
  };
}

interface AssessmentResult {
  attempt_id: string;
  assessment_id: string;
  started_at: string;
  completed_at: string;
  status: string;
  time_spent_minutes: number;
  responses: UserResponse[];
  assessment: {
    title: string;
    description: string;
  };
}

const AssessmentResultsPage: React.FC = () => {
  const { assessmentId, attemptId } = useParams<{ assessmentId: string; attemptId: string }>();
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'feedback'>('details');

  // Add a simple test to make sure component is loading
  console.log('AssessmentResultsPage component loaded!');

  useEffect(() => {
    console.log('AssessmentResultsPage mounted with params:', { assessmentId, attemptId });
    if (attemptId) {
      fetchAssessmentResult();
    } else {
      console.error('No attemptId provided');
      setError('No attempt ID provided');
      setLoading(false);
    }
  }, [attemptId]);

  const fetchAssessmentResult = async () => {
    try {
      console.log('Fetching assessment result for attemptId:', attemptId);
      setLoading(true);
      const response = await authAxios.get(`/api/assessments/attempts/${attemptId}`);
      console.log('Assessment result response:', response.data);
      console.log('Assessment object:', response.data.assessment);
      setResult(response.data);
    } catch (err: any) {
      console.error('Error fetching assessment result:', err);
      setError(err.response?.data?.detail || 'Failed to load assessment results');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  console.log('Render state:', { loading, error, result: !!result });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-white">Loading assessment results...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-red-900 border border-red-700 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-300">Error Loading Results</h3>
              <div className="mt-2 text-sm text-red-200">{error}</div>
              <div className="mt-4">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-gray-800 border border-gray-600 rounded-md p-4">
          <div className="text-center">
            <h3 className="text-lg font-medium text-white mb-2">No Results Found</h3>
            <p className="text-gray-300 mb-4">
              Assessment results not found for attempt ID: {attemptId}
            </p>
            <Link
              to="/assessments"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
            >
              Back to Assessments
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Assessment Feedback</h1>
            <p className="text-gray-300">{result?.assessment?.title || 'Loading assessment...'}</p>
            <p className="text-gray-500 text-sm">Completed on {new Date(result.completed_at).toLocaleDateString()}</p>
          </div>
          <Link
            to="/assessments"
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
          >
            Back to Assessments
          </Link>
        </div>
      </div>

      {/* Assessment Info */}
      <div className="bg-gray-800 rounded-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-white mb-2">
              {formatDuration(result.time_spent_minutes)}
            </div>
            <div className="text-gray-400 text-sm">Time Spent</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-white mb-2">
              {result.responses.length}
            </div>
            <div className="text-gray-400 text-sm">Questions Answered</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          {[
            { id: 'details', label: 'Question Details', icon: '📝' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="space-y-4">
          {result.responses.map((response, index) => (
            <div key={response.response_id} className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-white mb-2">
                    Question {index + 1}
                  </h3>
                  <p className="text-gray-300">
                    {response.generated_question?.question_text || 'Question text not available'}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <div className="mb-3">
                  <span className="text-sm font-medium text-gray-400">Your Answer:</span>
                  <div className="mt-1 p-3 bg-gray-700 rounded text-white text-sm">
                    {response.user_answer || 'No answer provided'}
                  </div>
                </div>

                {response.ai_feedback && (
                  <div>
                    <span className="text-sm font-medium text-gray-400">AI Feedback:</span>
                    <div className="mt-1 p-3 bg-blue-900 bg-opacity-30 rounded text-blue-200 text-sm">
                      {response.ai_feedback}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssessmentResultsPage;
